import { handleApiError } from './apiService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Season API Service
 * Handles all season-related API calls
 */
export class SeasonService {
  /**
   * Get current season from server
   * @returns {Promise<{current_season: string, timestamp: string}>}
   */
  static async getCurrentSeason() {
    try {
      const response = await fetch(`${API_URL}/season/current`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get current season');
      }

      return result.data;
    } catch (error) {
      console.error('❌ Error getting current season:', error);
      handleApiError(error);
      throw error;
    }
  }

  /**
   * Get all available seasons
   * @returns {Promise<{current_season: string, available_seasons: string[], total_seasons: number}>}
   */
  static async getAvailableSeasons() {
    try {
      const response = await fetch(`${API_URL}/season/available`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get available seasons');
      }

      return result.data;
    } catch (error) {
      console.error('❌ Error getting available seasons:', error);
      handleApiError(error);
      throw error;
    }
  }

  /**
   * Validate season format
   * @param {string} season - Season to validate (e.g., "2024-25")
   * @returns {Promise<{season: string, valid: boolean, start_year: number, end_year: number}>}
   */
  static async validateSeason(season) {
    try {
      const response = await fetch(`${API_URL}/api/season/validate/${encodeURIComponent(season)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // For validation, we might get 400 for invalid seasons
        if (response.status === 400) {
          const result = await response.json();
          return { valid: false, error: result.error, message: result.message };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        return { valid: false, error: result.error, message: result.message };
      }

      return result.data;
    } catch (error) {
      console.error('❌ Error validating season:', error);
      handleApiError(error);
      throw error;
    }
  }
}

export default SeasonService;